import { Backdrop, Box, CircularProgress } from "@mui/material";
import React, { Suspense, lazy, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { cfpBaseUrl } from "../../network/urls";
import { getDate, getMonthName, getYear } from "../../services/Functions";
import "./CfcDashboard.scss";
const DashboardHeader = lazy(() => import("../../components/cfcDashboard/DashboardHeader"));
const InfoCard = lazy(() => import("../../components/cfcDashboard/InfoCard"));

const CfcDashboard = () => {
  const [viewBackdrop, setViewBackdrop] = useState(false);

  const { token, code } = useSelector(state => state.auth)
  const [dashboardData, setDashboardData] = useState([]);
  const [dynamicServices, setDynamicServices] = useState([]);

  // Color palette for dynamic service cards
  const cardColors = ["#FFE2E5", "#EBDBFE", "#FEF5C0", "#FBE2CC", "#DCFCE7", "#CCE5FF", "#F8D7DA", "#E2E3E5"];

  // Service mapping for grouping related services
  const serviceMapping = {
    'Change of Name': 'Change of Attributes',
    'Change of Load': 'Change of Attributes',
    'Change of Tariff': 'Change of Attributes',
    'Change Of Attributes': 'Change of Attributes',
  };

  // Helper to split service name into title1 and title2
  const splitServiceName = (serviceName) => {
    const words = serviceName.split(' ');
    if (words.length === 1) {
      return { title1: serviceName, title2: '' };
    } else if (words.length === 2) {
      return { title1: words[0], title2: words[1] };
    } else {
      const midPoint = Math.ceil(words.length / 2);
      return {
        title1: words.slice(0, midPoint).join(' '),
        title2: words.slice(midPoint).join(' ')
      };
    }
  };

  const [serviceCards, setServiceCards] = useState([])

  useEffect(() => {
    const callCFCDashboardAPI = async () => {
      setViewBackdrop(true)
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);

      const formdata = new FormData();
      formdata.append("officeCode", code);

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow"
      };

      await fetch(cfpBaseUrl + "comman/getCFCDashboardStats", requestOptions)
        .then((response) => response.json())
        .then((result) => {
          if (result.status === "200") {

            setDashboardData(result.data);

            if (result.data) {
              const yearlyData = result.data.filter(item => item.Duration === 'Progressive')
              const currentMonthData = result.data.filter(item => item.Duration === 'CurrentMonth')

              // Get unique services and apply mapping for grouped services
              const serviceDataMap = {};
              const uniqueServicesSet = new Set();

              result.data.forEach(item => {
                const mappedService = serviceMapping[item.Service] || item.Service;
                uniqueServicesSet.add(mappedService);
              });

              yearlyData.forEach(data => {
                const mappedService = serviceMapping[data.Service] || data.Service;
                if (!serviceDataMap[mappedService]) {
                  serviceDataMap[mappedService] = { yearly: 0, monthly: 0 };
                }
                serviceDataMap[mappedService].yearly += data.Total;
              });

              currentMonthData.forEach(data => {
                const mappedService = serviceMapping[data.Service] || data.Service;
                if (!serviceDataMap[mappedService]) {
                  serviceDataMap[mappedService] = { yearly: 0, monthly: 0 };
                }
                serviceDataMap[mappedService].monthly += data.Total;
              });

              // Set dynamic services list for info cards
              const uniqueServicesList = Array.from(uniqueServicesSet);
              setDynamicServices(uniqueServicesList);

              // Generate dynamic service cards
              const dynamicServiceCards = Object.keys(serviceDataMap).map((serviceName, index) => {
                const { title1, title2 } = splitServiceName(serviceName);
                return {
                  service: serviceName,
                  title1,
                  title2,
                  yearlyCounter: serviceDataMap[serviceName].yearly,
                  month: new Date().toLocaleString('default', { month: 'short' }),
                  monthlyCounter: serviceDataMap[serviceName].monthly,
                  color: cardColors[index % cardColors.length],
                };
              });

              setServiceCards(dynamicServiceCards);
            }
          }
        })
        .catch((error) => console.error(error));
      setViewBackdrop(false);
    };
    callCFCDashboardAPI();
  }, [token]);

  return (
    <>
      <Box className="form-box">
        <div className="cfc-dashboard">
          <Suspense fallback={<div>Loading header...</div>}>
            <DashboardHeader
              title="Customer Services"
              serviceCards={serviceCards}
            />
          </Suspense>
          <div className="info-cards">
            <Suspense fallback={<div>Loading card...</div>}>
              <InfoCard title="Services" type="services" rows={dynamicServices.map(service => ({ service }))} />
              <InfoCard
                title="Progressive"
                subTitle={getYear()}
                columns={["Pending", "Attended", "Total"]}
                rows={getAlignedDataByDurationDynamic(dashboardData, "Progressive", dynamicServices, serviceMapping)}
                type="progressive"
              />
              <InfoCard
                title="Monthly"
                subTitle={getMonthName(0)}
                columns={["Pending", "Attended", "Total"]}
                rows={getAlignedDataByDurationDynamic(dashboardData, "CurrentMonth", dynamicServices, serviceMapping)}
                type="progressive"
              />
              <InfoCard
                title="Daily"
                subTitle={getDate()}
                columns={["Pending", "Attended", "Total"]}
                rows={getAlignedDataByDurationDynamic(dashboardData, "Daily", dynamicServices, serviceMapping)}
                type="progressive"
              />
            </Suspense>
          </div>
        </div>
      </Box>

      {/* Backdrop */}
      < Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={viewBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop >

    </>
  );
};

// Dynamic helper function to align data with services list
// Supports service mapping (e.g., Change of Name/Load/Tariff -> Change of Attributes)
export const getAlignedDataByDurationDynamic = (dashboardData, duration, servicesList, serviceMapping = {}) => {
  const filteredData = dashboardData.filter(item => item.Duration === duration);

  // Create a map to aggregate data for mapped services
  const serviceDataMap = {};
  servicesList.forEach(service => {
    serviceDataMap[service] = { pending: 0, attended: 0, total: 0 };
  });

  filteredData.forEach(item => {
    const mappedService = serviceMapping[item.Service] || item.Service;
    if (serviceDataMap[mappedService]) {
      serviceDataMap[mappedService].pending += item.Pending || 0;
      serviceDataMap[mappedService].attended += item.Attended || 0;
      serviceDataMap[mappedService].total += item.Total || 0;
    }
  });

  return servicesList.map(serviceName => ({
    service: serviceName,
    pending: serviceDataMap[serviceName].pending || null,
    attended: serviceDataMap[serviceName].attended || null,
    total: serviceDataMap[serviceName].total || null
  }));
};

export default CfcDashboard;
